/**
 * `slotAdmin` — the pure data + id-minting behind the GM's USERS page. Kept out
 * of the component so the id-minting rule and the GM gate are unit-testable without a
 * render harness (mirrors `gmDirectives.ts`, the same split for `GmDirectivePanel`).
 *
 * **Slot ids are caller-minted**, exactly like every other `create_*` id in the app —
 * the reducer never generates one (`packages/domain/src/game/commands.ts`'s header).
 * But a slot id is NOT like a pilot/mech id (an opaque UUID nobody ever sees): it's
 * user-visible — it's what a player picks from the login dropdown and what
 * `lvtt game --slot <id>` addresses — so a readable slug beats an opaque UUID here.
 * {@link mintSlotId} slugifies the typed name (mirrors `packages/domain/src/licenses.ts`'s
 * license-id `slug()` — lower-case, collapse non-alphanumerics to hyphens) and, on
 * collision with an already-defined slot, appends `-2`, `-3`, … until free.
 *
 * **Collision handling is real, not hypothetical.** `define_slot` on an existing slot id
 * is a hard reducer reject (`define_slot: slot ${id} already exists`,
 * `packages/domain/src/game/reducer.ts`), and the room applies a rejected `game_command`
 * as a **silent no-op** (`backend/src/gameRoom.ts`'s `applyWireCommand` — logs a warning,
 * sends only `game_undo_state`, no `game_change`, no error reply). So there is no
 * server-side error channel the UI could react to after the fact — the mint MUST avoid
 * the collision BEFORE sending, by checking the live, broadcast-driven `session.slots`
 * (never a stale local copy).
 */

/** lower-case + collapse non-alphanumerics to hyphens, trimmed (mirrors `licenses.ts`'s `slug()`). */
function slugify(name: string): string {
  const s = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return s === "" ? "slot" : s; // a name that slugifies to nothing (symbols/emoji only) still gets an id
}

/**
 * Mint a fresh, collision-free slot id from a typed name against the live set of
 * already-defined slot ids. Deterministic (same name + same existing set → same id),
 * so it's unit-testable without any randomness — unlike `crypto.randomUUID()`, which
 * would make the "what happens on collision" question moot rather than answered.
 */
export function mintSlotId(name: string, existingIds: readonly string[]): string {
  const base = slugify(name);
  if (!existingIds.includes(base)) return base;
  let n = 2;
  while (existingIds.includes(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

/**
 * Whether the slot-admin surface is available to this session role — GM-only (mirrors
 * `gmDirectives.ts`'s `canDirect`). This is the UI affordance gate; the server's
 * `GameRoom.handle` gate (`isGm`) is the real wall, and `defineSlot` itself is a no-op
 * unless the socket is authenticated.
 */
export function canAdminSlots(role: "gm" | "player" | null): boolean {
  return role === "gm";
}

/**
 * Whether `slotId` is the room's ONLY `gm`-role slot — the exact condition BOTH `remove_slot`
 * and `set_slot_role` (demoting to `"player"`) hard-reject on
 * (`packages/domain/src/game/reducer.ts`: each checks `if (prev.role === "gm") { const gmCount
 * = … ; if (gmCount <= 1) return err(…) }`). Deriving it here from the same `{role}`-shaped list
 * the reducer itself counts (not a hand-reinvented rule) is what "UI mirrors the reducer's rules
 * as affordances; it never re-enforces them" (CLAUDE.md) means for both commands:
 * `GmSlots.tsx` calls this to withhold the remove control AND the demote-to-player
 * control for that one slot, rather than sending a command doomed to be a silent server-side
 * no-op. Promoting a player to `gm`, or a `gm`→`gm` no-op relabel, are never blocked by either
 * this predicate or the reducer — only the gm→player transition on the room's sole GM slot is.
 */
export function isOnlyGmSlot(slots: readonly { id: string; role: "gm" | "player" }[], slotId: string): boolean {
  const slot = slots.find((s) => s.id === slotId);
  if (slot === undefined || slot.role !== "gm") return false;
  return slots.filter((s) => s.role === "gm").length <= 1;
}
